import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';

export default function ReferenceLiterature({ selectedChemical, selectedMaterial }) {
  const [searchTerm, setSearchTerm] = useState('');

  const references = [
    {
      id: 1,
      title: 'Chemical Resistance Guide for Stainless Steel Alloys',
      source: 'ASTM International',
      year: 2023,
      type: 'Standard',
      relevance: 95,
      citation: 'ASTM A240/A240M-23',
    },
    {
      id: 2,
      title: 'Corrosion Data Survey - Metals Section',
      source: 'NACE International',
      year: 2022,
      type: 'Database',
      relevance: 88,
      citation: 'NACE MR0175/ISO 15156',
    },
    {
      id: 3,
      title: 'Polymer Compatibility in Chemical Environments',
      source: 'Journal of Materials Science',
      year: 2023,
      type: 'Research Paper',
      relevance: 82,
      citation: 'DOI: 10.1007/s10853-023-xxxxx',
    },
    {
      id: 4,
      title: 'Safety Data Sheet - Sulfuric Acid 98%',
      source: 'Chemical Manufacturer',
      year: 2024,
      type: 'SDS',
      relevance: 90,
      citation: 'SDS-H2SO4-001',
    },
  ];

  const databases = [
    {
      name: 'ChemDATA Pro',
      description: 'Comprehensive chemical compatibility database',
      url: 'https://chemdata.example.com',
      icon: 'Database',
    },
    {
      name: 'MatWeb',
      description: 'Material property data and compatibility',
      url: 'https://matweb.com',
      icon: 'Globe',
    },
    {
      name: 'NIST Chemistry WebBook',
      description: 'Chemical and physical property data',
      url: 'https://webbook.nist.gov',
      icon: 'BookOpen',
    },
    {
      name: 'Cole-Parmer Chemical Compatibility',
      description: 'Interactive chemical compatibility charts',
      url: 'https://coleparmer.com',
      icon: 'Link',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Search References */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="BookOpen" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">Reference Literature</h3>
            <p className="text-sm text-muted-foreground">
              Supporting documentation and test data
            </p>
          </div>
        </div>

        <div className="relative mb-6">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            placeholder="Search references by title, source, or citation..."
            className="pl-10"
          />
        </div>

        <div className="space-y-3">
          {references?.map((ref) => (
            <div key={ref?.id} className="p-4 border border-border rounded-lg hover:border-primary transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">{ref?.title}</h4>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Building" size={14} />
                      {ref?.source}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      {ref?.year}
                    </span>
                    <span className="px-2 py-1 bg-accent rounded text-xs">{ref?.type}</span>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-sm font-medium text-foreground mb-1">Relevance</div>
                  <div className="text-lg font-bold text-primary">{ref?.relevance}%</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <code className="text-xs text-muted-foreground">{ref?.citation}</code>
                <div className="flex gap-2">
                  <button className="btn-secondary text-sm py-1 px-3">
                    <Icon name="Eye" size={14} className="mr-1" />
                    View
                  </button>
                  <button className="btn-secondary text-sm py-1 px-3">
                    <Icon name="Download" size={14} className="mr-1" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* External Databases */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            <Icon name="Link" size={24} className="text-blue-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">External Databases</h3>
            <p className="text-sm text-muted-foreground">
              Linked resources for additional compatibility data
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {databases?.map((db, index) => (
            <div key={index} className="p-4 border border-border rounded-lg hover:border-primary transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-accent rounded-lg">
                  <Icon name={db?.icon} size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">{db?.name}</h4>
                  <p className="text-sm text-muted-foreground mb-3">{db?.description}</p>
                  <a
                    href={db?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    Visit Database
                    <Icon name="ExternalLink" size={14} />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Citation Manager */}
      <div className="card bg-accent/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground mb-1">Citation Manager</h3>
            <p className="text-sm text-muted-foreground">
              {references?.length} references available for export
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="Copy" size={18} />
              Copy Citations
            </button>
            <button className="btn-primary flex items-center gap-2">
              <Icon name="Download" size={18} />
              Export Bibliography
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}